import React, { useState } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../hooks/useConfig';
import { useProgress } from '../hooks/useProgress';
import LessonCard from '../components/LessonCard';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './LessonsList.css';

/**
 * LessonsList Page
 * Shows all lessons organized by category
 */
function LessonsList() {
  const history = useHistory();
  const { lessons } = useConfig();
  const { getLessonProgress, isLessonCompleted } = useProgress();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = lessons?.categories || [];
  const allLessons = lessons?.lessons || [];

  const getFilteredLessons = () => {
    if (selectedCategory === 'all') {
      return allLessons;
    }
    return allLessons.filter(lesson => lesson.categoryId === selectedCategory);
  };

  const isLessonLocked = (lesson, index) => {
    // First lesson is always unlocked
    if (index === 0) return false;
    
    // Lesson is unlocked if previous lesson is completed
    const prevLesson = allLessons[index - 1];
    return prevLesson && !isLessonCompleted(prevLesson.id);
  };

  // Use the getLessonProgress from the hook directly
  const lessonProgress = (lessonId) => {
    return getLessonProgress ? getLessonProgress(lessonId) : 0;
  };

  const handleLessonClick = (lesson) => {
    history.push(`/lessons/${lesson.id}`);
  };

  const meta = seoService.getPageMeta('lessons');

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
      />
      <IonPage>
        <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Lessons</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="lessons-list-content">
        {/* Category Filter - Custom Implementation */}
        <div className="category-filter-container">
          <div className="category-filter-scroll">
            <button
              className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon} {category.title}
              </button>
            ))}
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="lessons-container">
          {/* Category Header (if specific category selected) */}
          {selectedCategory !== 'all' && (
            <div className="category-header">
              {categories.find(c => c.id === selectedCategory)?.description}
            </div>
          )}

          {/* Lessons List */}
          <div className="lessons-list">
            {getFilteredLessons().map((lesson, index) => {
              const globalIndex = allLessons.indexOf(lesson);
              const locked = isLessonLocked(lesson, globalIndex);
              const completed = isLessonCompleted(lesson.id);
              
              return (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  isLocked={locked}
                  isCompleted={completed}
                  progress={getLessonProgress(lesson.id)}
                  onClick={() => !locked && handleLessonClick(lesson)}
                />
              );
            })}
          </div>

          {/* Empty State */}
          {getFilteredLessons().length === 0 && (
            <div className="empty-state">
              <p>No lessons in this category yet.</p>
            </div>
          )}
        </div>

        {/* Ad Banner */}
        <AdBanner position="bottom" />
      </IonContent>
    </IonPage>
    </>
  );
}

export default LessonsList;